import { Key, useEffect } from 'react';
import ComponentRenderer from '../component-renderer';
import Layout from '../layout';
import FooterButton from '../button-footer';
import { observer } from 'mobx-react-lite';
import style from './style.module.scss';
import { useCallProvider } from '../../lib/hooks/useCallProvider';
import { ComponentDefinition } from '../../lib/typegen/component-definition';
import { usePageRendererHooks } from './hooks';
import { store } from '../../lib/stores/store';
import logger from '../../lib/helpers/logger';
import MaintenancePage from '../page-maintenance';
import useSystemAvailability from '../../lib/hooks/useSystemAvailability';
import useDeviceAvailability from '../../lib/hooks/useDeviceAvailability';
import UnavailablePage from '../page-unavailable';
import { KioskState } from '../../lib/typegen/kiosk-state';
import { devices } from '../../lib/devices/devices';
import { isKioAppAvailable } from '../../lib/helpers/kioApp';
import CashBoxAlarmPage from '../page-cash-box-alarm';
import InitialLoader from '../initial-loader';
import OutOfServicePage from '../page-out-of-service';

const PageRenderer = observer(function PageRenderer() {
	const { config, getString, currentPage, isConfigLoaded, handleNavigation, pageParams, isKioskAuthorized, canCancel } = usePageRendererHooks();
	const callProviderGroupId = store.configStore.callProviderGroupId;
	const { isGroupAvailable, isCallActive, callGroup } = useCallProvider(callProviderGroupId);
	const kioskState = useSystemAvailability();
	const isDeviceAvailable = useDeviceAvailability({ enabled: isConfigLoaded });

	useEffect(() => {
		if (isConfigLoaded) store.sessionStore.clearActiveSessionId();
	}, [isConfigLoaded]);

	useEffect(() => {
		if (currentPage?.forceHideBackButton) store.uiStore.setBackEnabled(false);
		return () => store.uiStore.setBackEnabled(true);
	}, [currentPage]);

	useEffect(() => {
		store.uiStore.setShowCart(currentPage?.showCart ?? false);
	}, [currentPage]);

	useEffect(() => {
		if (!isKioskAuthorized && isConfigLoaded) {
			logger.debug.error('Kiosk not authorized to use the application');
		}
	}, [isKioskAuthorized, isConfigLoaded]);

	useEffect(() => {
		if (!currentPage && isConfigLoaded) {
			logger.logGeneral.error({}, 'Page not found:', currentPage);
		}
	}, [currentPage, isConfigLoaded]);

	useEffect(() => {
		logger.logGeneral.log({}, 'Check sensor effect evaluated', { isConfigLoaded, ignoreCheckSensor: config?.ignoreCheckSensor });
		if (!isConfigLoaded || config.ignoreCheckSensor) return;
		if (isKioAppAvailable() && currentPage?.id === config.pages[0].id && devices.checkReader.checkInserted()) {
			logger.logGeneral.warn({}, 'Check detected in reader on startup, sending alert');
			store.kioskStore.sendCheckAlert(`CAPresent`, `Check present in reader on startup`);
		}
	}, [config?.pages, config?.ignoreCheckSensor, currentPage?.id, isConfigLoaded, isDeviceAvailable]);

	if (!isKioskAuthorized || !isConfigLoaded || !currentPage) {
		return <InitialLoader />;
	}

	if (pageParams) {
		const invoiceType = pageParams.find((param) => param.key === 'invoiceType')?.value;
		if (invoiceType) {
			store.accountStore.setInvoiceType(Number.parseInt(invoiceType));
		}
		const cardType = pageParams.find((param) => param.key === 'cardType')?.value;
		if (cardType) {
			store.accountStore.setCardType(Number.parseInt(cardType));
		}
	}

	if (kioskState === KioskState.CashBoxAlarm)
		return (
			<Layout
				key={'cash-box-alarm'}
				mainContent={<CashBoxAlarmPage />}
				logoProps={{ src: config.logoSrc, alt: getString(config.logoAlt) }}
				navProps={{ handleNavigation }}
			/>
		);

	if (kioskState === KioskState.OutOfService)
		return (
			<Layout
				key={'out-of-service'}
				mainContent={<OutOfServicePage />}
				logoProps={{ src: config.logoSrc, alt: getString(config.logoAlt) }}
				navProps={{ handleNavigation }}
			/>
		);

	if (kioskState === KioskState.Unavailable)
		return (
			<Layout
				key={'unavailable'}
				mainContent={<UnavailablePage />}
				logoProps={{ src: config.logoSrc, alt: getString(config.logoAlt) }}
				navProps={{ handleNavigation }}
			/>
		);

	if (kioskState === KioskState.Maintenance)
		return (
			<Layout
				key={'maintenance'}
				mainContent={<MaintenancePage />}
				logoProps={{ src: config.logoSrc, alt: getString(config.logoAlt) }}
				navProps={{ handleNavigation }}
			/>
		);

	return (
		<Layout
			key={'app'}
			logoProps={{ src: config.logoSrc, alt: getString(config.logoAlt) }}
			mainContent={currentPage.components?.map((component: ComponentDefinition, idx: Key | null | undefined) => {
				return <ComponentRenderer key={idx} component={component} handleNavigation={handleNavigation} pageParams={pageParams} />;
			})}
			navProps={{
				previousButtonProps: {
					innerText: getString(config.backButtonTextContent, currentPage?.id === 'languages' ? false : currentPage?.noTranslate),
					flow: 'back',
				},
				nextButtonProps: {
					innerText: getString(config.nextButtonTextContent, currentPage?.id === 'languages' ? false : currentPage?.noTranslate),
					flow: 'next',
				},
				previousPageId: currentPage.previousPageId,
				nextPageId: currentPage.nextPageId,
				handleNavigation,
			}}
			footerContent={
				<div className={style.footer}>
					<FooterButton
						theme='exit'
						disabled={!canCancel}
						innerText={getString(config.cancelButtonTextContent, currentPage?.id === 'languages' ? false : currentPage?.noTranslate)}
						onClick={async () => await store.sessionStore.startEndSessionTasks()}
					/>
					{isGroupAvailable && !isCallActive && callProviderGroupId && (
						<FooterButton
							theme='call'
							innerText={getString(config.callButtonTextContent, currentPage?.id === 'languages' ? false : currentPage?.noTranslate)}
							onClick={callGroup}
						/>
					)}
				</div>
			}
		/>
	);
});

export default PageRenderer;
